import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Camera, Users, AlertTriangle, Activity } from "lucide-react";
import { http } from "@/lib/api";

const Sparkline = ({ points = [], height = 50 }) => {
  if (points.length < 2) {
    return <div className="h-12 grid-bg" />;
  }
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const W = 320;
  const H = height;
  const step = W / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${H - ((p.value - min) / span) * (H - 4) - 2}`)
    .join(" ");
  const last = points[points.length - 1];
  const lx = (points.length - 1) * step;
  const ly = H - ((last.value - min) / span) * (H - 4) - 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <path d={`${d} L ${W} ${H} L 0 ${H} Z`} fill="hsl(var(--primary) / 0.15)" />
      <path d={d} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <circle cx={lx} cy={ly} r="3" fill="hsl(var(--primary))" />
    </svg>
  );
};

const sevColor = (s) =>
  s === "critical" ? "sev-critical" :
  s === "high"     ? "sev-high" :
  s === "medium"   ? "sev-medium" :
                     "sev-low";

export const ZoneDetailsDrawer = ({ zoneId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!zoneId) { setData(null); return; }
    let alive = true;
    setLoading(true);
    http.get(`/zones/${zoneId}`)
      .then((r) => { if (alive) setData(r.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [zoneId]);

  const z = data?.zone;
  const pct = z ? Math.round(z.pct * 100) : 0;

  return (
    <Sheet open={!!zoneId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-[420px] sm:max-w-[480px] p-0 border-l border-border bg-[hsl(var(--surface))] text-foreground"
        data-testid="zone-drawer"
      >
        <SheetHeader className="px-4 py-3 border-b border-border">
          <SheetTitle className="font-display font-black uppercase tracking-tight text-base">
            {z?.name ?? "Loading…"}
          </SheetTitle>
          <SheetDescription className="micro-label">
            zone · {z?.type ?? "—"} · id {z?.id ?? "—"}
          </SheetDescription>
        </SheetHeader>

        {!loading && data && (
          <div className="overflow-y-auto h-[calc(100vh-64px)] divide-y divide-border">
            {/* KPIs */}
            <section className="p-4 grid grid-cols-2 gap-3">
              <div>
                <div className="micro-label">Occupancy</div>
                <div className="font-mono text-2xl font-semibold tracking-tighter">
                  {(z?.current_occupancy ?? 0).toLocaleString("en-IN")}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {pct}% of {(z?.capacity ?? 0).toLocaleString("en-IN")}
                </div>
                <div className="mt-2 h-1.5 bg-border w-full">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${pct}%`,
                      background: pct >= 85 ? "hsl(var(--destructive))" : pct >= 70 ? "hsl(var(--warning))" : "hsl(var(--primary))",
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="micro-label">Risk Weight</div>
                <div className="font-mono text-2xl font-semibold tracking-tighter">
                  {z?.risk_weight?.toFixed(2) ?? "—"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  multiplier on density
                </div>
              </div>
            </section>

            {/* Sparkline */}
            <section className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="h-widget flex items-center gap-1">
                  <Activity className="w-3.5 h-3.5" /> Occupancy · last 20 min
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {data.history?.length ?? 0} pts
                </span>
              </div>
              <div className="panel-elevated p-2">
                <Sparkline points={data.history ?? []} />
              </div>
            </section>

            {/* Cameras */}
            <section className="p-4">
              <div className="h-widget flex items-center gap-1 mb-2">
                <Camera className="w-3.5 h-3.5" /> Cameras in zone · {data.cameras?.length ?? 0}
              </div>
              <div className="space-y-1">
                {(data.cameras ?? []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-2 py-1.5 panel-elevated text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${c.status === "online" ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`}
                      />
                      <span className="font-mono">{c.id}</span>
                      <span className="truncate text-muted-foreground">{c.name}</span>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {c.ptz ? "PTZ" : "Fixed"}
                    </span>
                  </div>
                ))}
                {!data.cameras?.length && (
                  <div className="text-xs text-muted-foreground">No cameras assigned.</div>
                )}
              </div>
            </section>

            {/* Recent events */}
            <section className="p-4">
              <div className="h-widget flex items-center gap-1 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 sev-critical" /> Recent events · {data.recent_events?.length ?? 0}
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {(data.recent_events ?? []).map((e) => (
                  <div key={e.id} className="px-2 py-1.5 panel-elevated text-xs">
                    <div className="flex items-center justify-between">
                      <span className={`font-mono text-[10px] uppercase tracking-widest ${sevColor(e.severity)}`}>
                        {e.severity}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {new Date(e.ts).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="font-display font-bold mt-0.5 leading-tight">{e.title}</div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2">{e.description}</div>
                  </div>
                ))}
                {!data.recent_events?.length && (
                  <div className="text-xs text-muted-foreground">No events in this zone yet.</div>
                )}
              </div>
            </section>

            {/* People count strip */}
            <section className="p-4 flex items-center gap-3">
              <Users className="w-4 h-4 text-muted-foreground" />
              <div className="font-mono text-xs text-muted-foreground">
                Realtime occupancy stream — {data.history?.length ?? 0} samples buffered, drift &lt;5%/tick.
              </div>
            </section>
          </div>
        )}
        {loading && (
          <div className="p-6 font-mono text-xs text-muted-foreground">Loading zone telemetry…</div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ZoneDetailsDrawer;
